import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useSite } from "@/contexts/SiteContext";
import { useTraining } from "@/contexts/TrainingContext";
import TrialBanner from "@/components/TrialBanner";
import {
  LayoutDashboard,
  Thermometer,
  PackageOpen,
  Sparkles,
  GraduationCap,
  Bug,
  ShieldCheck,
  ChefHat,
  ClipboardCheck,
  Printer,
  Boxes,
  History,
  TrendingUp,
  Settings as SettingsIcon,
  LogOut,
  MapPin,
  ScanLine,
  HelpCircle,
  AlertTriangle,
  Usb,
  CreditCard,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import OfflinePill from "@/components/OfflinePill";
import { useTecor } from "@/contexts/TecorContext";
import { usePrintHub } from "@/contexts/PrintHubContext";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/scan", label: "Scan", icon: ScanLine },
  { to: "/temperature", label: "Temperature", icon: Thermometer },
  { to: "/receival", label: "Receival", icon: PackageOpen },
  { to: "/cleaning", label: "Cleaning", icon: Sparkles },
  { to: "/checklists", label: "Checklists", icon: ClipboardCheck },
  { to: "/labels", label: "Label Printer", icon: Printer },
  { to: "/products", label: "Product Library", icon: Boxes },
  { to: "/print-log", label: "Print Log", icon: History },
  { to: "/print-hub", label: "Print Hub", icon: Usb, role: "admin" },
  { to: "/scorecard", label: "Supplier Scorecard", icon: TrendingUp },
  { to: "/recipes", label: "Recipes & Allergens", icon: ChefHat },
  { to: "/haccp", label: "HACCP / CCP", icon: ShieldCheck },
  { to: "/pest", label: "Pest Control", icon: Bug },
  { to: "/training", label: "Training", icon: GraduationCap },
  { to: "/help", label: "Help", icon: HelpCircle },
  { to: "/billing", label: "Billing", icon: CreditCard, role: "admin" },
  { to: "/settings", label: "Settings", icon: SettingsIcon },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { sites, activeSite, selectSite } = useSite();
  const { trainingMode, setTrainingMode } = useTraining();
  const { supported, connected, deviceName, printerStatus, connect, openDriverHelp } = useTecor();
  const { hubEnabled, hubPending, hubWorking } = usePrintHub();
  const navigate = useNavigate();
  const location = useLocation();
  const lastNudge = useRef(0);

  // Billing owners are finance-only: keep them on Billing / payment pages.
  useEffect(() => {
    if (user?.role === "billing_owner") {
      const p = location.pathname;
      if (!(p.startsWith("/billing") || p.startsWith("/payment"))) {
        navigate("/billing", { replace: true });
      }
    }
  }, [user, location.pathname, navigate]);

  // Global nudge when the backend blocks a write because the trial expired.
  useEffect(() => {
    const onExpired = (e) => {
      const now = Date.now();
      if (now - lastNudge.current < 6000) return; // debounce bursts
      lastNudge.current = now;
      toast.error(e.detail?.detail || "This site's free trial has ended.", {
        action: user?.role === "admin"
          ? { label: "Subscribe", onClick: () => navigate("/billing") }
          : undefined,
      });
    };
    window.addEventListener("haccp-trial-expired", onExpired);
    return () => window.removeEventListener("haccp-trial-expired", onExpired);
  }, [navigate, user]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen grain-bg text-charcoal">
      <TrialBanner />
      {/* Training-mode persistent banner — high-contrast so nobody
          mistakes their sandbox practise for real audit data. */}
      {trainingMode && (
        <div
          data-testid="training-banner"
          className="bg-amber-400 text-amber-950 text-sm font-bold px-4 py-2 flex items-center justify-center gap-2 sticky top-0 z-50"
        >
          <AlertTriangle className="w-4 h-4" />
          <span>Training Mode is ON — nothing you do is saved to the real audit ledger.</span>
          <button
            data-testid="training-banner-off"
            onClick={() => setTrainingMode(false)}
            className="underline ml-2 hover:no-underline"
          >
            Turn off
          </button>
        </div>
      )}
      {/* Top bar */}
      <header className="h-16 bg-eucalyptus-900 text-white flex items-center justify-between px-3 sm:px-6 shadow-sm sticky top-0 z-40">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-ochre-600 grid place-items-center font-display font-extrabold text-white">
            H
          </div>
          <div className="hidden sm:block">
            <div className="font-display font-bold text-lg leading-none">HACCP AU</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-eucalyptus-100">
              Food Safety Compliance
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          {supported && (() => {
            const blocked = connected && printerStatus === "blocked";
            const onClick = blocked
              ? () => openDriverHelp()
              : connected
                ? undefined
                : () => connect().catch(() => {});
            return (
              <button
                data-testid="header-printer-pill"
                onClick={onClick}
                title={
                  blocked
                    ? "Windows is blocking the printer — click for the driver fix"
                    : connected
                      ? `Printer ready: ${deviceName}`
                      : "Click to connect a label printer"
                }
                className={`hidden md:flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 transition-colors ${
                  blocked
                    ? "bg-amber-500 text-amber-950 hover:bg-amber-400"
                    : connected
                      ? "bg-eucalyptus-700 text-white cursor-default"
                      : "bg-eucalyptus-800/60 text-eucalyptus-100 hover:bg-eucalyptus-700"
                }`}
              >
                {blocked ? <AlertTriangle className="w-3.5 h-3.5" /> : <Usb className="w-3.5 h-3.5" />}
                <span className="max-w-[140px] truncate" data-testid="header-printer-name">
                  {blocked ? "Driver needed" : connected ? deviceName : "No printer"}
                </span>
                <span
                  className={`w-2 h-2 rounded-full ${
                    blocked ? "bg-red-700" : connected ? "bg-emerald-300" : "bg-white/40"
                  }`}
                  aria-hidden
                />
              </button>
            );
          })()}
          <OfflinePill />

          {/* Training Mode topbar toggle — visible to every authenticated
              user (kitchen managers may run a training shift even though
              individual staff don't have the role to edit Settings). */}
          <label
            className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold cursor-pointer select-none"
            data-testid="training-toggle-wrap"
            title="Practise without saving to the real audit ledger"
          >
            <span className={`hidden sm:inline ${trainingMode ? "text-amber-300" : "text-eucalyptus-100"}`}>
              Training
            </span>
            <Switch
              data-testid="training-toggle"
              checked={trainingMode}
              onCheckedChange={setTrainingMode}
              aria-label="Toggle training mode"
            />
          </label>

          {sites.length > 0 && activeSite && (
            <div className="flex items-center gap-2 min-w-0" data-testid="site-selector">
              <MapPin className="hidden sm:block w-4 h-4 text-eucalyptus-100" />
              <Select
                value={activeSite.id}
                onValueChange={(v) => {
                  const s = sites.find((x) => x.id === v);
                  if (s) selectSite(s);
                }}
              >
                <SelectTrigger
                  data-testid="site-selector-trigger"
                  className="bg-eucalyptus-700 border-eucalyptus-700 text-white h-10 w-[160px] sm:w-[260px] overflow-hidden [&>span]:truncate"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {sites.map((s) => (
                    <SelectItem key={s.id} value={s.id} data-testid={`site-option-${s.id}`}>
                      <span className="font-medium">{s.name}</span>
                      <span className="text-xs text-charcoal-soft ml-2 capitalize">
                        {s.type.replace("_", " ")}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {user?.role === "admin" && hubEnabled && (
            <NavLink
              to="/print-hub"
              data-testid="hub-header-chip"
              className="hidden sm:flex items-center gap-1.5 rounded-full bg-eucalyptus-700/60 hover:bg-eucalyptus-700 px-3 py-1.5 text-xs font-medium transition-colors"
              title="Print Hub is running in the background"
            >
              <span className={`w-2 h-2 rounded-full ${connected ? "bg-eucalyptus-300 animate-pulse" : "bg-amber-400"}`} />
              Print Hub{hubWorking ? " · printing…" : hubPending > 0 ? ` · ${hubPending} waiting` : " · live"}
            </NavLink>
          )}

          <div className="text-right hidden sm:block">
            <div className="text-sm font-medium">{user?.name}</div>
            <div className="text-[10px] uppercase tracking-widest text-eucalyptus-100">
              {user?.role}
            </div>
          </div>
          <Button
            data-testid="logout-btn"
            onClick={handleLogout}
            variant="ghost"
            size="icon"
            className="text-white hover:bg-eucalyptus-700"
          >
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-60 shrink-0 bg-white border-r border-[color:var(--border)] min-h-[calc(100vh-64px)] px-3 py-6 sticky top-16 hidden md:block">
          <div className="label-eyebrow px-4 mb-3">Navigation</div>
          <nav className="flex flex-col gap-1">
            {NAV.filter((it) => (user?.role === "billing_owner" ? it.to === "/billing" : (!it.role || it.role === user?.role))).map(({ to, label, icon: Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                data-testid={`nav-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
              >
                <Icon className="w-4 h-4" />
                <span>{label}</span>
                {to === "/print-hub" && hubPending > 0 && (
                  <span
                    data-testid="nav-hub-badge"
                    title={`${hubPending} label${hubPending === 1 ? "" : "s"} waiting to print`}
                    className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-ochre-600 text-white text-[11px] font-bold leading-none"
                  >
                    {hubPending > 99 ? "99+" : hubPending}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>
        </aside>

        {/* Main */}
        <main className="flex-1 p-6 md:p-8 max-w-[1400px] mx-auto w-full">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
